<script lang="ts">
  import { onMount, onDestroy } from 'svelte'
  import { copyElementStyles, TEXTAREA_STYLE_PROPS } from '$lib/utils/textHighlight'
  
  interface Props {
    textareaElement: HTMLTextAreaElement | null
    selectionStart: number
    selectionEnd: number
    isVisible: boolean
  }
  
  let { textareaElement, selectionStart, selectionEnd, isVisible }: Props = $props()
  
  let overlayElement = $state<HTMLDivElement | null>(null)
  let highlightSpans: HTMLSpanElement[] = $state([])
  let lastStyleCopy = 0
  
  function updateHighlight() {
    if (!textareaElement || !overlayElement || !isVisible || selectionStart === selectionEnd) {
      return
    }
    
    // Copy styles from textarea
    const now = Date.now()
    if (now - lastStyleCopy > 100) {
      lastStyleCopy = now
      copyElementStyles(textareaElement, overlayElement, TEXTAREA_STYLE_PROPS)
    }
    
    const text = textareaElement.value
    const beforeText = text.substring(0, selectionStart)
    const selectedText = text.substring(selectionStart, selectionEnd)
    const afterText = text.substring(selectionEnd)
    
    // Clear existing highlights
    overlayElement.innerHTML = ''
    
    // Create spans for text segments
    const beforeSpan = document.createElement('span')
    beforeSpan.textContent = beforeText
    beforeSpan.className = 'transparent-text'
    
    const selectedSpan = document.createElement('span')
    selectedSpan.textContent = selectedText
    selectedSpan.className = 'selection-highlight'
    
    const afterSpan = document.createElement('span')
    afterSpan.textContent = afterText
    afterSpan.className = 'transparent-text'
    
    // Append spans to overlay
    overlayElement.appendChild(beforeSpan)
    overlayElement.appendChild(selectedSpan)
    overlayElement.appendChild(afterSpan)
    
    // Sync scroll position
    overlayElement.scrollTop = textareaElement.scrollTop
    overlayElement.scrollLeft = textareaElement.scrollLeft
  }
  
  function syncScroll() {
    if (textareaElement && overlayElement) {
      overlayElement.scrollTop = textareaElement.scrollTop
      overlayElement.scrollLeft = textareaElement.scrollLeft
    }
  }
  
  // Update highlight when props change
  $effect(() => {
    updateHighlight()
  })
  
  onMount(() => {
    if (textareaElement) {
      textareaElement.addEventListener('scroll', syncScroll, { passive: true })
      updateHighlight()
    }
  })
  
  onDestroy(() => {
    if (textareaElement) {
      textareaElement.removeEventListener('scroll', syncScroll)
    }
  })
</script>

{#if isVisible && textareaElement && selectionStart !== selectionEnd}
  <div class="selection-overlay" bind:this={overlayElement}>
    <!-- Content will be dynamically inserted -->
  </div>
{/if}

<style>
  .selection-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    overflow: hidden;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    padding: inherit;
    margin: 0;
    border: 0;
    color: transparent;
    background: transparent;
  }
  
  :global(.selection-overlay .transparent-text) {
    color: transparent;
  }
  
  :global(.selection-overlay .selection-highlight) {
    background: rgba(79, 70, 229, 0.3);
    border-radius: 2px;
    color: transparent;
  }
  
  /* Dark mode */
  :global(.dark-theme .selection-overlay .selection-highlight) {
    background: rgba(147, 145, 255, 0.3);
  }
</style>